/**
 * Keeps the whole main menu on screen at any window size: when the menu is taller than the window,
 * the top bar (the logo in it), the warnings under it and the menu columns are zoomed down together until everything fits
 * (to 72% at most, 0.85: 45% made its text 7 to 10 pixels on a laptop's screen; below that the menu scrolls). Runs on resize and whenever the menu's content
 * grows, e.g. when the registration panel grows after registering.
 */
const MIN_ZOOM = 0.72;
/**
 * a phone or a small screen (the columns stacked in one, 800px wide or less; a touch screen; a screen 540px tall or
 * less, as a phone on its side): zoomed down only a little, then the menu scrolls. Fitted to the height, a phone's long
 * stack came out at 45% (its text about 6px), and a phone on its side, wider than 800px, at 46% to 54%: unusable
 */
const MIN_ZOOM_SMALL = 0.85;
const SMALL = "(max-width: 800px), (pointer: coarse), (max-height: 540px)";

export function setUpMenuFit(): void {
    const splash = document.getElementById("splash-ui");
    // (1.05: the logo is in the top bar, zoomed with it)
    const parts = ["menu-top", "splash-hw-acceleration-warning", "splash-rotate-message", "splash-modals"]
        .map(id => document.getElementById(id))
        .filter((el): el is HTMLElement => el !== null);
    if (!splash || parts.length === 0) return;

    let fitting = false;
    let queued: "grown" | "all" | undefined;
    let zoom = 1;
    let shown = false;
    /** each part's style attribute as our own zoom left it: a change to it after that is the page's (a warning shown) */
    const ours = new WeakMap<HTMLElement, string | null>();
    const apply = (value: number): void => {
        zoom = value;
        for (const part of parts) {
            part.style.zoom = value === 1 ? "" : value.toFixed(3);
            ours.set(part, part.getAttribute("style"));
        }
    };
    /**
     * `all`: worked out again from the full size (a resize, the menu shown again); `grown`: only made smaller when the
     * content no longer fits (it never grows back between resizes: the menu doesn't change size under the mouse every
     * time a line of its text changes)
     */
    const fit = (): void => {
        const how = queued;
        queued = undefined;
        if (splash.clientHeight === 0) { // the menu is hidden (in a match)
            shown = false;
            return;
        }
        fitting = true;
        const available = splash.clientHeight;
        const floor = window.matchMedia?.(SMALL).matches ? MIN_ZOOM_SMALL : MIN_ZOOM;
        if (how === "all" || !shown) {
            apply(1);
            const needed = splash.scrollHeight;
            apply(needed > available + 1 ? Math.max(floor, (available - 6) / needed) : 1);
        } else if (splash.scrollHeight > available + 1 && zoom > floor) {
            // (the content at the current zoom: what it would need at 1, so the new zoom is worked out the same way)
            const needed = splash.scrollHeight / zoom;
            apply(Math.max(floor, Math.min(zoom, (available - 6) / needed)));
        } else if (zoom < 1 && splash.scrollHeight < available * 0.85) {
            // much shorter now (another room picked, a long line gone): as big again as it fits (0.85: it stayed small
            // until the window was resized)
            apply(1);
            const needed = splash.scrollHeight;
            apply(needed > available + 1 ? Math.max(floor, (available - 6) / needed) : 1);
        }
        shown = true;
        fitting = false;
    };
    const queue = (how: "grown" | "all"): void => {
        if (fitting) return;
        if (queued) {
            if (how === "all") queued = "all";
            return;
        }
        queued = how;
        requestAnimationFrame(fit);
    };

    window.addEventListener("resize", () => queue("all"));
    // our own zoom changes on the zoomed elements are not content changes (a warning shown or hidden is)
    new MutationObserver(records => {
        if (records.every(record => {
            const part = record.target as HTMLElement;
            return record.type === "attributes" && parts.includes(part) && record.attributeName === "style" && ours.get(part) === part.getAttribute("style");
        })) return;
        queue("grown");
    }).observe(splash, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ["style", "class", "hidden"] });
    queue("all");
}

/** the smallest a link's name is drawn to keep its words whole (px) */
const LINK_NAME_MIN = 11;

/**
 * The links under the server (1.05.2: a row of tiles, menu.scss's #home-information-buttons) keep each word of their
 * names whole: a name with a word too long for its tile (Spanish "Estadísticas", Russian "Статистика", on a narrow
 * menu) is drawn smaller, half a pixel at a time down to LINK_NAME_MIN, rather than broken in the middle of the word;
 * only one still too long at that may break (`tight`). Worked out again whenever a tile's width changes (the window
 * resized, a link shown or hidden: the row has a column for each link shown, the menu zoomed), its words change (a
 * language) or the menu's fonts come.
 */
export function setUpLinkNames(): void {
    const row = document.getElementById("home-information-buttons");
    if (!row) return;
    const fit = (): void => {
        for (const name of Array.from(row.querySelectorAll<HTMLElement>(".btn > span"))) {
            name.style.fontSize = "";
            name.classList.remove("tight");
            if (name.offsetParent === null || name.clientWidth === 0) continue;
            let size = parseFloat(getComputedStyle(name).fontSize);
            while (name.scrollWidth > name.clientWidth + 1 && size > LINK_NAME_MIN) {
                size = Math.max(LINK_NAME_MIN, size - 0.5);
                name.style.fontSize = `${size}px`;
            }
            if (name.scrollWidth > name.clientWidth + 1) name.classList.add("tight");
        }
    };
    let queued = false;
    const queue = (): void => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
            queued = false;
            fit();
        });
    };
    // (a tile's width: its height changes with its name's size, which is ours)
    const widths = new WeakMap<Element, number>();
    if (typeof ResizeObserver !== "undefined") {
        const watch = new ResizeObserver(entries => {
            let changed = false;
            for (const entry of entries) {
                const width = Math.round(entry.contentRect.width);
                if (widths.get(entry.target) !== width) {
                    widths.set(entry.target, width);
                    changed = true;
                }
            }
            if (changed) queue();
        });
        for (const tile of Array.from(row.querySelectorAll(".btn"))) watch.observe(tile);
    }
    new MutationObserver(queue).observe(row, { subtree: true, childList: true, characterData: true });
    window.addEventListener("resize", queue);
    void document.fonts?.ready.then(queue);
    queue();
}
